/**
 * GitHub as a git provider (registry entry — see ./index.js).
 *
 * Everything GitHub-specific that git sync needs lives here: the account
 * (device flow, githubAuth.js), the REST client, the repo-reference parser,
 * create/list/tag, the noreply author address and the HTTPS username libgit2
 * sends with the token. Moved out of workspaceGitSync.js unchanged on
 * 01.10.2026 (git sync Stage 1, card 6 step 1).
 */
import axios from 'axios';
import { tauriAxiosAdapter } from '../../../utils/tauriAxiosAdapter';
import { getGitHubToken, getGitHubConnection } from '../../githubAuth';
import { WORKSPACE_TOPIC } from '../../../utils/appInfo';
import { listGitAccounts, getGitAccountToken } from '../gitAccounts';
import { MAX_REPOS, mergeRepos, newestFirst, ownersOf } from '../repoCatalog';

const API = 'https://api.github.com';
const WEB = 'https://github.com';

/** Dedicated GitHub API client (bypasses the Apiboo auth interceptor + CORS via Tauri adapter). */
function client(token) {
  return axios.create({
    baseURL: API,
    adapter: tauriAxiosAdapter,
    timeout: 15000,
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/vnd.github+json',
      'X-GitHub-Api-Version': '2022-11-28',
      'Content-Type': 'application/json',
    },
  });
}

function mapRepo(r) {
  const owner = r.owner?.login || '';
  return {
    fullName: r.full_name,
    name: r.name,
    owner,
    ownerKey: owner,
    ownerAvatar: r.owner?.avatar_url || '',
    ownerType: r.owner?.type === 'Organization' ? 'org' : 'user',
    private: Boolean(r.private),
    description: r.description || '',
    updatedAt: r.updated_at || '',
    pushedAt: r.pushed_at || '',
    url: r.clone_url || `${WEB}/${r.full_name}.git`,
    defaultBranch: r.default_branch || '',
  };
}

const PER_PAGE = 100;
const MAX_PAGES = 5; // 500 repositories / branches is plenty for a picker
const MAX_ORGS = 30; // organisations asked one by one for their repositories

/**
 * Every page of a list endpoint, up to `maxItems` (stops at the first short
 * page). `headers` are the first page's: GitHub reports the token's scopes,
 * its OAuth app and SSO state there.
 */
async function pages(http, path, params, maxItems = PER_PAGE * MAX_PAGES) {
  const items = [];
  let headers = {};
  for (let page = 1; page <= MAX_PAGES && items.length < maxItems; page += 1) {
    const res = await http.get(path, { params: { ...params, per_page: PER_PAGE, page } });
    if (page === 1) headers = res?.headers || {};
    const list = Array.isArray(res?.data) ? res.data : [];
    items.push(...list);
    if (list.length < PER_PAGE) break;
  }
  return { items: items.slice(0, maxItems), headers };
}

/** Every page of a list endpoint, up to MAX_PAGES (stops at the first short page). */
async function allPages(http, path, params) {
  return (await pages(http, path, params)).items;
}

/** A response header by name, whatever case the adapter kept. */
function header(headers, name) {
  if (!headers) return undefined;
  const want = name.toLowerCase();
  const key = Object.keys(headers).find((k) => k.toLowerCase() === want);
  return key === undefined ? undefined : headers[key];
}

/** What kind of token this is, from GitHub's documented prefixes (and the scopes header). */
export function githubTokenKind(token, scopesHeader) {
  const t = String(token || '');
  if (t.startsWith('github_pat_')) return 'fineGrained';
  if (t.startsWith('ghp_')) return 'classic';
  if (t.startsWith('gho_')) return 'oauth';
  if (t.startsWith('ghu_')) return 'app';
  return scopesHeader === undefined ? 'fineGrained' : 'oauth';
}

/** Scopes that let a token list private organisation memberships (`GET /user/orgs`). */
const ORG_SCOPES = ['read:org', 'write:org', 'admin:org', 'user'];

/** `X-GitHub-SSO` → { required: url } | { partial: [org ids] } | null. */
export function parseSsoHeader(value) {
  const v = String(value || '');
  if (!v) return null;
  const url = v.match(/url=(\S+)/);
  if (/required/i.test(v) && url) return { required: url[1].replace(/[;,]$/, '') };
  const ids = v.match(/organizations=([\d,\s]+)/);
  if (/partial-results/i.test(v)) return { partial: ids ? ids[1].split(',').map((x) => x.trim()).filter(Boolean) : [] };
  return null;
}

/** Where the user grants (or requests) the OAuth app access to their organisations. */
export function oauthAppSettingsUrl(clientId) {
  const id = clientId || process.env.REACT_APP_GITHUB_CLIENT_ID || '';
  return id ? `${WEB}/settings/connections/applications/${id}` : `${WEB}/settings/applications`;
}

function restrictedNotice(org, clientId) {
  return {
    kind: 'oauthRestricted',
    owner: org,
    url: oauthAppSettingsUrl(clientId),
    secondaryUrl: `${WEB}/organizations/${org}/settings/oauth_application_policy`,
  };
}

/** Why an organisation's repositories could not be listed, as a notice (see repoCatalog.js). */
function orgFailureNotice(err, org, ctx) {
  const res = err?.response;
  const sso = parseSsoHeader(header(res?.headers, 'x-github-sso'));
  if (sso?.required) return { kind: 'sso', owner: org, url: sso.required };
  const message = String(res?.data?.message || err?.message || '');
  if (/OAuth App access restrictions/i.test(message)) return restrictedNotice(org, ctx.clientId);
  if (ctx.tokenKind === 'fineGrained' && (res?.status === 403 || res?.status === 404)) {
    return { kind: 'fineGrained', owner: org, url: `${WEB}/settings/personal-access-tokens` };
  }
  return { kind: 'orgUnavailable', owner: org, status: res?.status || 0 };
}

/**
 * The repo picker's catalog (owner, 28.10.2026 — "show the organisations I
 * own", the way GitHub Desktop and VS Code do it):
 *
 * 1. `GET /user/repos?affiliation=owner,collaborator,organization_member`
 *    (paged to the 500 cap) — the user's repos plus every org repo GitHub
 *    lets this token see.
 * 2. `GET /user/orgs`, plus the owners of org repos from step 1 (an org with
 *    a private membership is missing from /user/orgs without `read:org`).
 * 3. `GET /orgs/{org}/repos?type=all` per org — repos the membership query
 *    leaves out. A failure here never fails the list; it becomes a notice:
 *    SAML SSO (403 + `X-GitHub-SSO`), OAuth app access restrictions (403
 *    with GitHub's message, or an OAuth token that sees no private repo of
 *    an org it belongs to — GitHub filters silently there), a fine-grained
 *    token limited to another resource owner.
 * 4. A classic PAT / OAuth token without `read:org` is told so.
 */
async function listCatalog(token) {
  const http = client(token);
  const first = await pages(http, '/user/repos', { affiliation: 'owner,collaborator,organization_member', sort: 'pushed' }, MAX_REPOS);
  const scopesHeader = header(first.headers, 'x-oauth-scopes');
  const scopes = String(scopesHeader || '').split(',').map((s) => s.trim()).filter(Boolean);
  const ctx = { tokenKind: githubTokenKind(token, scopesHeader), clientId: header(first.headers, 'x-oauth-client-id') || '' };
  const viaOAuthApp = ctx.tokenKind === 'oauth';
  const userRepos = first.items.map(mapRepo);
  const notices = [];

  // Who the user is (for the owner filter) and which orgs they belong to.
  let me = { login: '', avatarUrl: '' };
  try {
    const { data } = await http.get('/user');
    me = { login: data?.login || '', avatarUrl: data?.avatar_url || '' };
  } catch {
    // The login only orders the filter; the repos still list.
  }
  const orgs = new Map(); // lower(login) -> { login, avatarUrl, id }
  try {
    const listed = await allPages(http, '/user/orgs', {});
    listed.forEach((o) => o?.login && orgs.set(o.login.toLowerCase(), { login: o.login, avatarUrl: o.avatar_url || '', id: o.id }));
  } catch {
    // No read:org (fine-grained tokens, some PATs) — the repo owners below still name orgs.
  }
  userRepos.forEach((r) => {
    if (r.ownerType === 'org' && !orgs.has(r.owner.toLowerCase())) orgs.set(r.owner.toLowerCase(), { login: r.owner, avatarUrl: r.ownerAvatar, id: null });
  });

  // SSO "partial-results": orgs that hide their repos from this query until the token is authorized.
  const partial = parseSsoHeader(header(first.headers, 'x-github-sso'))?.partial || [];
  const ssoOrgs = new Set();
  partial.forEach((id) => {
    const org = [...orgs.values()].find((o) => String(o.id) === String(id));
    if (org) {
      ssoOrgs.add(org.login.toLowerCase());
      notices.push({ kind: 'sso', owner: org.login, url: `${WEB}/orgs/${org.login}/sso` });
    }
  });

  const orgLists = [];
  for (const org of [...orgs.values()].slice(0, MAX_ORGS)) {
    if (ssoOrgs.has(org.login.toLowerCase())) continue;
    try {
      const got = await pages(http, `/orgs/${encodeURIComponent(org.login)}/repos`, { type: 'all', sort: 'pushed' }, MAX_REPOS);
      const repos = got.items.map(mapRepo);
      if (!org.avatarUrl && got.items[0]?.owner?.avatar_url) org.avatarUrl = got.items[0].owner.avatar_url;
      orgLists.push(repos);
      const sso = parseSsoHeader(header(got.headers, 'x-github-sso'));
      const key = org.login.toLowerCase();
      const seesPrivate = repos.some((r) => r.private) || userRepos.some((r) => r.private && r.owner.toLowerCase() === key);
      if (sso?.required) {
        notices.push({ kind: 'sso', owner: org.login, url: sso.required });
      } else if (viaOAuthApp && !seesPrivate) {
        // An org that restricts OAuth apps answers with its public repos only — no error.
        notices.push(restrictedNotice(org.login, ctx.clientId));
      }
    } catch (err) {
      notices.push(orgFailureNotice(err, org.login, ctx));
    }
  }

  if ((ctx.tokenKind === 'classic' || viaOAuthApp) && scopesHeader !== undefined && !scopes.some((s) => ORG_SCOPES.includes(s))) {
    notices.push({
      kind: 'missingScope',
      owner: '',
      scope: 'read:org',
      tokenKind: ctx.tokenKind,
      url: ctx.tokenKind === 'classic' ? `${WEB}/settings/tokens` : '',
    });
  }
  if (ctx.tokenKind === 'fineGrained' && !notices.some((n) => n.kind === 'fineGrained')) {
    notices.push({ kind: 'fineGrained', owner: '', url: `${WEB}/settings/personal-access-tokens` });
  }

  const repos = mergeRepos(userRepos, ...orgLists).sort(newestFirst);
  const owners = ownersOf(repos, {
    login: me.login,
    avatarUrl: me.avatarUrl,
    extra: [...orgs.values()].map((o) => ({ key: o.login, login: o.login, avatarUrl: o.avatarUrl, type: 'org' })),
  });
  return { repos, owners, notices };
}

/**
 * GitHub with a personal access token (owner, 07.10.2026: "GitHub by token —
 * for the users that want to stay local without an account"). The browser
 * sign-in links GitHub to the Apiboo account at the IdP and so needs an Apiboo
 * sign-in; a token needs nothing but GitHub. It is kept like a GitLab token
 * (services/git/gitAccounts.js: OS keychain, never a file, never Apiboo).
 *
 * ONE GitHub account per device, whichever way it came: the token, when there
 * is one, is the GitHub account here — the Git page shows it in GitHub's own
 * place and every GitHub call uses it. Adding a token replaces an earlier
 * token; signing it out brings the browser-linked account back (if any).
 * A link connected through the browser account (its `accountId` is that
 * login) keeps using it while it is still linked.
 */
async function tokenAccount(git) {
  // An unreadable accounts file means no token, not no GitHub: the browser link still answers.
  const all = (await listGitAccounts('github').catch(() => [])).filter((a) => a.provider === 'github');
  if (!all.length) return null;
  const named = git?.accountId ? all.find((a) => a.id === git.accountId || a.login === git.accountId) : null;
  if (named) return named;
  if (git?.accountId && (await getGitHubConnection().catch(() => null))?.connected) return null;
  return all[0];
}

const github = {
  id: 'github',
  label: 'GitHub',
  icon: 'github',
  authKinds: ['device', 'token'],
  baseUrl: WEB,
  abilities: { createRepo: true, listRepos: true, listBranches: true, openInBrowser: true, deviceFlow: true, tagWorkspace: true },
  /** libgit2 sends the token as the HTTPS password under this user name (a PAT too). */
  httpsUsername: 'x-access-token',
  notLinkedCode: 'GITHUB_NOT_LINKED',
  /** A second token replaces the first: one GitHub account per device. */
  singleAccount: true,

  tokenPage() {
    return `${WEB}/settings/tokens`;
  },

  /** Who a token belongs to — used when adding it on the Git page. The server is always github.com. */
  async verifyToken(_server, token) {
    const { data } = await client(token).get('/user');
    return { login: data?.login, name: data?.name, avatarUrl: data?.avatar_url, userId: data?.id };
  },

  async getToken(git) {
    const pat = await tokenAccount(git);
    return pat ? getGitAccountToken(pat.id) : getGitHubToken();
  },

  /** { connected, login, avatarUrl } — plus `viaToken` and the account id for a token. */
  async getAccount(git) {
    const pat = await tokenAccount(git);
    if (!pat) return getGitHubConnection();
    return { connected: true, login: pat.login, avatarUrl: pat.avatarUrl, id: pat.id, userId: pat.userId, viaToken: true };
  },

  authorEmail(login) {
    return login ? `${login}@users.noreply.github.com` : 'noreply@apiboo.com';
  },

  repoUrl(owner, repo) {
    return `${WEB}/${owner}/${repo}.git`;
  },

  webUrl(git) {
    return git?.owner && git?.repo ? `${WEB}/${git.owner}/${git.repo}` : '';
  },

  /**
   * Parse a repo reference into { owner, repo, url }.
   * Accepts "owner/repo", "https://github.com/owner/repo(.git)", or "git@github.com:owner/repo.git".
   */
  parseRepo(input) {
    const s = String(input || '').trim();
    if (!s) return null;
    // Plain owner/repo (no scheme, no @)
    if (!s.includes('://') && !s.includes('@')) {
      const m = s.match(/^([\w.-]+)\/([\w.-]+?)(?:\.git)?\/?$/);
      if (m) return { owner: m[1], repo: m[2], url: github.repoUrl(m[1], m[2]) };
    }
    // Any github.com URL (https or ssh)
    const m = s.match(/github\.com[/:]([\w.-]+)\/([\w.-]+?)(?:\.git)?(?:[/?#].*)?$/i);
    if (m) return { owner: m[1], repo: m[2], url: github.repoUrl(m[1], m[2]) };
    return null;
  },

  /**
   * Create the repo under the authenticated user if it doesn't exist.
   * @returns {boolean} true if the repo already existed (so it may have content)
   *
   * NOTE: created with auto_init:true (an initial commit) on purpose — libgit2's
   * push to a completely EMPTY GitHub repo fails with HTTP 400. Starting with a
   * commit means the first connect is a normal (diverged → merge → push) sync.
   */
  async ensureRepoExists(token, owner, repo, isPrivate) {
    const gh = client(token);
    try {
      await gh.get(`/repos/${owner}/${repo}`);
      return true; // already exists
    } catch (e) {
      if (e.response?.status !== 404) throw e;
    }
    try {
      await gh.post('/user/repos', { name: repo, private: Boolean(isPrivate), auto_init: true });
      return false;
    } catch (e) {
      // 422 = name already exists on this account (e.g. private repo our token
      // couldn't see on the GET above). Treat as existing.
      if (e.response?.status === 422) return true;
      throw e;
    }
  },

  /** The linked user's repositories AND their organisations', most recently pushed first. */
  async listRepos(token) {
    return (await listCatalog(token)).repos;
  },

  /** { repos, owners, notices } for the repo picker — see listCatalog above and repoCatalog.js. */
  async listRepoCatalog(token) {
    return listCatalog(token);
  },

  /** A repository's branches: [{ name, protected }]. */
  async listBranches(token, owner, repo) {
    const list = await allPages(client(token), `/repos/${encodeURIComponent(owner)}/${encodeURIComponent(repo)}/branches`, {});
    return list.map((b) => ({ name: b.name, protected: Boolean(b.protected) }));
  },

  /**
   * Only repositories flagged as app workspaces (topic "<app>-workspace"), of
   * the user AND their organisations: GitHub ORs several `user:`/`org:`
   * qualifiers, so one search covers every owner.
   */
  async listWorkspaceRepos(token, login) {
    const http = client(token);
    let orgs = [];
    if (login) {
      try {
        orgs = (await allPages(http, '/user/orgs', {})).map((o) => o?.login).filter(Boolean);
      } catch {
        orgs = []; // no read:org — the user's own workspaces still list
      }
    }
    const owners = login ? [`user:${login}`, ...orgs.map((o) => `org:${o}`)] : [];
    const q = [`topic:${WORKSPACE_TOPIC}`, ...owners].join(' ');
    const { data } = await http.get('/search/repositories', {
      params: { q, per_page: 100, sort: 'updated' },
    });
    return (data?.items || []).map(mapRepo);
  },

  /** Add the workspace topic (idempotent, keeps existing topics). Best-effort — never throws. */
  async tagWorkspace(token, owner, repo) {
    try {
      if (!token || !owner || !repo) return;
      const gh = client(token);
      const { data } = await gh.get(`/repos/${owner}/${repo}/topics`);
      const names = Array.isArray(data?.names) ? data.names : [];
      if (names.includes(WORKSPACE_TOPIC)) return;
      await gh.put(`/repos/${owner}/${repo}/topics`, { names: [...names, WORKSPACE_TOPIC] });
    } catch {
      // best-effort; tagging failure must not block sync
    }
  },
};

export default github;
